<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/main/PosMainTablesGrid.vue,
  // simplified to admin's table statuses (no foreign/claims tones). Table view
  // models reuse the exact building logic of the old RestaurantPosTables.vue.
  import { computed, getCurrentInstance, toRef } from 'vue';
  import { IPosTableView } from '@/interfaces/IPosTableView';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { usePosTablesView } from '@/composition/use-pos-tables-view';
  import { useOrdersStore } from '@/stores/orders.store';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { usePosReservationTables } from '@/composition/reservations/use-pos-reservation-tables';
  import {
    getPosTableOccupiedSince,
    isPosTableOccupied,
  } from '@/domain/reservations/pos-table-hints';
  import type { IKeypadTableCandidate } from '@/utils/floor-keypad';
  import PosFloorPlan from '@/components/pages/restaurant/pos/PosFloorPlan.vue';
  import PosV2FloorKeypad from '@/components/pos-v2/main/PosV2FloorKeypad.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiScrollable from '@/components/pos-v2/ui/PosUiScrollable.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';

  const props = defineProps<{
    search: string;
  }>();

  const emit = defineEmits<{
    (e: 'table-click', table: IPosTableView): void;
    /** Long-press / right-click on a floor-plan tile (orders preview). */
    (e: 'table-preview', table: IPosTableView): void;
    (e: 'clear-search'): void;
  }>();

  const restaurantStore = useRestaurantStore();
  const ordersStore = useOrdersStore();
  const tablesView = usePosTablesView();
  const money = usePosMoney();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const isLoading = computed(
    () => !restaurantStore.tables || !ordersStore.pendingOrdersByType
  );

  /**
   * Same building logic as the old RestaurantPosTables.vue computed, including
   * its search semantics: orders are filtered BEFORE building the views, so
   * searching turns non-matching tables "empty" on the floor plan too.
   */
  const { tables, now } = usePosReservationTables(toRef(props, 'search'));

  /** List view shows only tables holding a matching order (-pos behavior). */
  const displayedTables = computed<IPosTableView[]>(() => {
    const all = tables.value ?? [];
    if (!props.search.trim()) return all;

    return all.filter((t) => t.orders.length > 0);
  });

  const hasSearchMiss = computed(
    () =>
      !!props.search.trim() &&
      (tables.value?.length ?? 0) > 0 &&
      displayedTables.value.length === 0
  );

  //#region Card display helpers

  function formatElapsedTime(sinceMs: number | null): string {
    if (!sinceMs) return '';
    const elapsed = Math.max(0, now.value.getTime() - sinceMs);
    const minutes = Math.floor(elapsed / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  }

  function formatCustomerNames(names: string[]): string {
    const unique = [...new Set(names)];
    if (unique.length === 0) return '';
    if (unique.length === 1) return unique[0];
    const joiner = i18nT('posV2.nameListJoiner');
    if (unique.length === 2) return `${unique[0]} ${joiner} ${unique[1]}`;
    const allButLast = unique.slice(0, -1).join(', ');
    return `${allButLast} ${joiner} ${unique[unique.length - 1]}`;
  }

  function reservationLabel(table: IPosTableView): string {
    const reservation = table.reservation;
    if (!reservation) return '';
    const seated = reservation.status === 'seated';
    const key =
      table.tableStatus === 'empty'
        ? seated
          ? 'cardSeated'
          : 'cardReserved'
        : seated
        ? 'tileSeated'
        : 'tileNext';
    return i18nT(`reservations.pos.${key}`, {
      time: reservation.timeLabel,
      name: reservation.guestName || i18nT('reservations.common.anonymous'),
      count: reservation.partySize,
    });
  }

  //#endregion

  function handleTableClick(table: IPosTableView) {
    emit('table-click', table);
  }

  //#region Floor keypad (plan view)

  /**
   * Temporarily hidden: the trigger costs a whole toolbar row for little
   * benefit. Flip to true to bring the open-by-number keypad back.
   */
  const SHOW_FLOOR_KEYPAD = false;

  /** Enabled tables only — the keypad opens tables you can actually seat. */
  const keypadCandidates = computed<IKeypadTableCandidate[]>(
    () =>
      restaurantStore.tables
        ?.filter((t) => t.enabled)
        .map((t) => ({ id: t.id, name: t.name })) ?? []
  );

  function handleKeypadSelect(tableId: number) {
    const view = tables.value?.find((t) => t.id === tableId);
    if (view) emit('table-click', view);
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-tables-grid">
    <!-- Loading -->
    <div v-if="isLoading" class="loading-state">
      <PosUiSkeleton v-for="i in 8" :key="i" class="table-skeleton" />
    </div>

    <!-- No tables at all -->
    <PosUiEmptyState
      v-else-if="!tables || !tables.length"
      icon="table-furniture"
      :title="$t('posV2.noTablesTitle')"
      :description="$t('posV2.noTablesDescription')"
    >
      <template #action>
        <PosUiButton
          color="primary"
          variant="soft"
          size="lg"
          icon="plus"
          @click="
            $router.push(`/${restaurantStore.restaurant.stringId}/tables`)
          "
        >
          {{ $t('posV2.createTables') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <!-- Floor plan view -->
    <template
      v-else-if="
        tablesView.effectiveView.value === 'plan' &&
        tablesView.planAvailable.value
      "
    >
      <div v-if="SHOW_FLOOR_KEYPAD" class="plan-toolbar">
        <PosV2FloorKeypad
          :tables="keypadCandidates"
          @select="handleKeypadSelect"
        />
      </div>

      <PosUiScrollable
        class="plan-container"
        orientation="vertical"
        variant="overlay"
        theme="page"
        size="sm"
        scroll-class="plan-scroll"
      >
        <PosFloorPlan
          :sectors="tablesView.sectors.value"
          :tables="tables"
          @table-click="handleTableClick"
          @table-longpress="emit('table-preview', $event)"
        />
      </PosUiScrollable>
    </template>

    <!-- Plan view chosen but no floor plan yet: CTA -->
    <div v-else-if="tablesView.effectiveView.value === 'plan'" class="plan-cta">
      <div class="cta-card">
        <div class="cta-icon">
          <PosUiIcon name="floor-plan" :size="28" />
        </div>
        <h3 class="cta-title">{{ $t('posTables.planCtaTitle') }}</h3>
        <p class="cta-description">{{ $t('posTables.planCtaText') }}</p>
        <PosUiButton
          color="primary"
          variant="solid"
          size="lg"
          icon="floor-plan"
          @click="
            $router.push(`/${restaurantStore.restaurant.stringId}/tables/plan`)
          "
        >
          {{ $t('posTables.planCtaButton') }}
        </PosUiButton>
      </div>
    </div>

    <!-- Search without matches -->
    <PosUiEmptyState
      v-else-if="hasSearchMiss"
      icon="magnify"
      :title="$t('posV2.noResults')"
    >
      <template #action>
        <PosUiButton
          color="neutral"
          variant="outline"
          size="lg"
          icon="close"
          @click="emit('clear-search')"
        >
          {{ $t('posV2.clearSearch') }}
        </PosUiButton>
      </template>
    </PosUiEmptyState>

    <!-- List view -->
    <PosUiScrollable
      v-else
      class="tables-container"
      orientation="vertical"
      variant="overlay"
      theme="page"
      size="md"
      scroll-class="tables-scroll"
    >
      <button
        v-for="table in displayedTables"
        :key="table.id"
        type="button"
        class="table-card"
        :class="{
          'status-free': !isPosTableOccupied(table),
          'status-occupied':
            table.tableStatus === 'pending' ||
            (table.tableStatus === 'empty' &&
              table.reservation?.status === 'seated'),
          'status-reserved':
            table.tableStatus === 'empty' &&
            table.reservation &&
            table.reservation.status !== 'seated',
          'status-late': table.reservation?.late,
          'status-finished': table.tableStatus === 'finished',
          'status-paid': table.tableStatus === 'paid',
        }"
        @click="handleTableClick(table)"
      >
        <div class="card-header">
          <div class="title">
            <PosUiIcon name="table-furniture" class="table-icon" />
            <h3 class="table-name">{{ table.name }}</h3>
          </div>
          <span v-if="table.names.length" class="customer-names">
            {{ formatCustomerNames(table.names) }}
          </span>
        </div>

        <div v-if="isPosTableOccupied(table)" class="card-body">
          <div class="orders-count">
            <span class="count-number">{{ table.orders.length }}</span>
            <span class="count-label">
              {{
                table.orders.length === 1
                  ? $t('posV2.orderWord')
                  : $t('posV2.ordersWord')
              }}
            </span>
          </div>
          <div class="card-footer">
            <span class="footer-item">
              <PosUiIcon name="clock-outline" class="footer-icon" />
              {{ formatElapsedTime(getPosTableOccupiedSince(table)) }}
            </span>
            <span class="footer-item">
              <PosUiIcon name="cash" class="footer-icon" />
              {{ money.formatComputedMoney(table.total) }}
            </span>
          </div>
        </div>

        <div v-else class="card-body empty">
          <span v-if="!table.reservation" class="empty-label">{{
            $t('posV2.available')
          }}</span>
        </div>
        <PosUiBadge
          v-if="
            table.reservation &&
            table.tableStatus !== 'empty' &&
            table.reservation.status !== 'seated'
          "
          color="info"
          size="xs"
          class="reservation-next"
          :label="reservationLabel(table)"
        />
        <div v-else-if="table.reservation" class="reservation-footer">
          <PosUiIcon name="clock-outline" />
          <span>{{ reservationLabel(table) }}</span>
        </div>
      </button>
    </PosUiScrollable>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-tables-grid {
    @apply tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden;

    .loading-state {
      @apply tw-grid tw-gap-3 tw-p-4;
      grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));

      .table-skeleton {
        @apply tw-min-h-[120px] tw-rounded-xl;
      }
    }

    .plan-toolbar {
      @apply tw-flex tw-flex-none tw-justify-end tw-px-4 tw-pt-4;
    }

    .plan-container {
      @apply tw-relative tw-min-h-0 tw-flex-1;

      :deep(.plan-scroll) {
        @apply tw-h-full tw-overflow-y-auto tw-p-4;
      }
    }

    .plan-cta {
      @apply tw-flex tw-flex-1 tw-items-center tw-justify-center tw-p-6;

      .cta-card {
        @apply tw-flex tw-w-full tw-max-w-md tw-flex-col tw-items-center tw-gap-2 tw-rounded-xl tw-border-2 tw-border-dashed tw-border-neutral-300 tw-bg-white tw-px-8 tw-py-10 tw-text-center;

        .cta-icon {
          @apply tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-orange-50 tw-text-orange-600;
        }

        .cta-title {
          @apply tw-text-lg tw-font-semibold tw-text-neutral-900;
        }

        .cta-description {
          @apply tw-mb-3 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
        }
      }
    }

    .tables-container {
      @apply tw-relative tw-min-h-0 tw-flex-1;

      :deep(.tables-scroll) {
        @apply tw-grid tw-auto-rows-min tw-gap-3 tw-overflow-y-auto tw-p-4;
        // Container width drives the column count (viewport breakpoints
        // overshoot next to the admin sidebar + incoming pane).
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      }
    }

    // Flat status tints (amber = in progress, sky = to collect, emerald =
    // paid) with 2px status borders — same language as the floor-plan tiles.
    .table-card {
      @apply tw-relative tw-flex tw-min-h-[104px] tw-cursor-pointer tw-select-none tw-flex-col tw-items-stretch tw-rounded-xl tw-border-2 tw-p-3 tw-text-left tw-transition-colors tw-duration-150;

      &.status-free {
        @apply tw-border-neutral-200 tw-bg-white;

        &:hover {
          @apply tw-border-neutral-300 tw-bg-neutral-50;
        }
      }

      &.status-occupied {
        @apply tw-border-amber-300 tw-bg-amber-50;

        &:hover {
          @apply tw-border-amber-400 tw-bg-amber-100;
        }
      }

      &.status-finished {
        @apply tw-border-sky-300 tw-bg-sky-50;

        &:hover {
          @apply tw-border-sky-400 tw-bg-sky-100;
        }
      }

      &.status-paid {
        @apply tw-border-emerald-300 tw-bg-emerald-50;

        &:hover {
          @apply tw-border-emerald-400 tw-bg-emerald-100;
        }
      }

      &.status-reserved {
        @apply tw-border-dashed tw-border-sky-300 tw-bg-sky-50/40;
        &:hover {
          @apply tw-border-sky-400 tw-bg-sky-50;
        }
      }
      &.status-late {
        @apply tw-border-l-red-400;
      }
      .reservation-footer {
        @apply tw-mt-2 tw-flex tw-items-start tw-gap-1 tw-text-xs tw-font-medium tw-text-sky-700;
        span {
          @apply tw-min-w-0 tw-break-words;
        }
      }
      .reservation-next {
        @apply tw-mt-2 tw-self-start;
      }
      &.status-occupied .reservation-footer {
        @apply tw-text-amber-800;
      }

      .card-header {
        @apply tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2;

        .title {
          @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2;

          .table-icon {
            @apply tw-flex-none tw-text-base tw-text-neutral-400;
          }

          .table-name {
            @apply tw-truncate tw-text-[15px] tw-font-semibold tw-text-neutral-900;
          }
        }

        .customer-names {
          @apply tw-max-w-[120px] tw-truncate tw-text-xs tw-text-neutral-500;
        }
      }

      .card-body {
        @apply tw-mt-1 tw-flex tw-w-full tw-flex-1 tw-flex-col tw-gap-1;

        &.empty {
          @apply tw-mt-0 tw-items-center tw-justify-center;

          .empty-label {
            @apply tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral-400;
          }
        }

        .orders-count {
          @apply tw-flex tw-items-baseline tw-gap-1 tw-text-sm;

          .count-number {
            @apply tw-text-base tw-font-bold tw-text-neutral-900;
          }

          .count-label {
            @apply tw-font-normal tw-text-neutral-500;
          }
        }

        .card-footer {
          @apply tw-mt-auto tw-flex tw-w-full tw-items-center tw-border-t tw-border-neutral-900/5 tw-pt-2;

          .footer-item {
            @apply tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-neutral-600;

            // Money: right-aligned, tabular, strongest weight in the row.
            &:last-child {
              @apply tw-ml-auto tw-font-semibold tw-tabular-nums tw-text-neutral-900;
            }

            .footer-icon {
              @apply tw-text-sm;
            }
          }
        }
      }
    }
  }
</style>
